import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Navbar: React.FC = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMenuOpen]);

    // Inline Contact CTA inside the centered pill (Figma: small glass button, Body S 14px)
    const inlinePillCtaStyle: React.CSSProperties = {
        padding: '6px 14px',
        borderRadius: '10px',
        background: 'linear-gradient(180deg, rgba(61, 66, 68, 0) -40.91%, rgba(158, 170, 176, 0.248) 132.95%)',
        boxShadow: 'inset 0px 8px 10px rgba(246, 249, 255, 0.08)',
        filter: 'drop-shadow(0px 6px 40px rgba(255, 255, 255, 0.08))',
        backdropFilter: 'blur(50px)',
        fontFamily: 'Inter',
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '26px',
        letterSpacing: '-0.0001em',
        color: '#FFFFFF',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        textDecoration: 'none',
        transition: 'all 0.3s ease',
    };

    // Right-side primary CTA — Apply (filled glass per Figma)
    const primaryAuthStyle: React.CSSProperties = {
        width: '170px',
        height: '58px',
        padding: '12px 30px',
        borderRadius: '10px',
        background: 'linear-gradient(180deg, rgba(61, 66, 68, 0) -40.91%, rgba(158, 170, 176, 0.248) 132.95%)',
        boxShadow: 'inset 0px 8px 10px rgba(246, 249, 255, 0.08)',
        filter: 'drop-shadow(0px 6px 40px rgba(255, 255, 255, 0.08))',
        backdropFilter: 'blur(50px)',
        fontFamily: 'Inter',
        fontWeight: 400,
        fontSize: '24px',
        lineHeight: '26px',
        letterSpacing: '-0.0001em',
        color: '#FFFFFF',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        textDecoration: 'none',
        transition: 'all 0.3s ease',
    };

    // Right-side secondary CTA — Log in (transparent glass per Figma, no fill)
    const secondaryAuthStyle: React.CSSProperties = {
        ...primaryAuthStyle,
        background: 'transparent',
    };

    return (
        <header className="relative z-50 pt-8 sm:pt-6">
            <div className="w-full px-[31px] sm:px-12">
                {/* Mobile Layout - Height: 35px, Logo: 77px × 35px, Padding: 31px */}
                <div className={`lg:hidden flex items-center justify-between h-[35px] ${isMenuOpen ? 'fixed top-8 left-0 right-0 px-[31px] z-50' : 'relative z-50'}`}>
                    <Link to="/" onClick={() => setIsMenuOpen(false)}>
                        <img src="/branding/logo_blue.svg" alt="Hush" className="relative z-50 cursor-pointer" style={{ width: '77px', height: '35px', objectFit: 'contain' }} />
                    </Link>
                    <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="text-white relative z-50"
                        style={{
                            width: '40px',
                            height: '40px',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: isMenuOpen ? '1.5px solid rgba(255, 255, 255, 0.5)' : 'none',
                            borderRadius: '50%',
                            background: isMenuOpen ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                            transition: 'all 0.3s ease'
                        }}
                        aria-label="Toggle menu"
                    >
                        <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {isMenuOpen ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            )}
                        </svg>
                    </button>
                </div>

                {/* Mobile Menu - Fullscreen overlay */}
                <div
                    className={`lg:hidden fixed inset-0 z-40 overflow-hidden transition-all duration-300 ease-in-out ${
                        isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
                    }`}
                    style={{
                        background: 'linear-gradient(130.58deg, #3d4547 4.58%, #022334 105.75%)',
                        backdropFilter: 'blur(0px)',
                        top: '0'
                    }}
                >
                    <nav className={`flex flex-col px-0 pt-[150px] overflow-hidden transition-all duration-300 ease-in-out ${
                        isMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
                    }`} style={{ gap: '15px' }}>
                            <Link
                                to="/"
                                onClick={() => setIsMenuOpen(false)}
                                className="text-white hover:text-white/80 transition-colors px-8"
                                style={{
                                    fontFamily: 'Manrope',
                                    fontWeight: 700,
                                    fontSize: '24px',
                                    lineHeight: '150%',
                                    letterSpacing: '-0.011em'
                                }}
                            >
                                Home
                            </Link>
                            <Link
                                to="/candidates"
                                onClick={() => setIsMenuOpen(false)}
                                className="text-white hover:text-white/80 transition-colors px-8"
                                style={{
                                    fontFamily: 'Manrope',
                                    fontWeight: 700,
                                    fontSize: '24px',
                                    lineHeight: '150%',
                                    letterSpacing: '-0.011em'
                                }}
                            >
                                Candidates
                            </Link>
                            <Link
                                to="/employers"
                                onClick={() => setIsMenuOpen(false)}
                                className="text-white hover:text-white/80 transition-colors px-8"
                                style={{
                                    fontFamily: 'Manrope',
                                    fontWeight: 700,
                                    fontSize: '24px',
                                    lineHeight: '150%',
                                    letterSpacing: '-0.011em'
                                }}
                            >
                                Employers
                            </Link>
                            <Link
                                to="/features"
                                onClick={() => setIsMenuOpen(false)}
                                className="text-white hover:text-white/80 transition-colors px-8"
                                style={{
                                    fontFamily: 'Manrope',
                                    fontWeight: 700,
                                    fontSize: '24px',
                                    lineHeight: '150%',
                                    letterSpacing: '-0.011em'
                                }}
                            >
                                Features
                            </Link>
                            <Link
                                to="/contact"
                                onClick={() => setIsMenuOpen(false)}
                                className="text-white hover:text-white/80 transition-colors px-8"
                                style={{
                                    fontFamily: 'Manrope',
                                    fontWeight: 700,
                                    fontSize: '24px',
                                    lineHeight: '150%',
                                    letterSpacing: '-0.011em'
                                }}
                            >
                                Contact
                            </Link>

                            {/* Auth Buttons */}
                            <div className="flex flex-col gap-4 px-8 mt-8">
                                <Link
                                    to="/auth/apply"
                                    onClick={() => setIsMenuOpen(false)}
                                    style={{
                                        padding: '12px 24px',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(255, 255, 255, 0.15)',
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        boxShadow: '0px 4px 10px 0px rgba(0, 0, 0, 0.1)',
                                        backdropFilter: 'blur(20px)',
                                        fontFamily: 'Manrope',
                                        fontSize: '16px',
                                        fontWeight: 400,
                                        lineHeight: '20px',
                                        color: 'white',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        textDecoration: 'none'
                                    }}
                                >
                                    Apply
                                </Link>
                                <Link
                                    to="/auth/login"
                                    onClick={() => setIsMenuOpen(false)}
                                    style={{
                                        padding: '12px 24px',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(255, 255, 255, 0.15)',
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        boxShadow: '0px 4px 10px 0px rgba(0, 0, 0, 0.1)',
                                        backdropFilter: 'blur(20px)',
                                        fontFamily: 'Manrope',
                                        fontSize: '16px',
                                        fontWeight: 400,
                                        lineHeight: '20px',
                                        color: 'white',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        textDecoration: 'none'
                                    }}
                                >
                                    Log in
                                </Link>
                            </div>
                        </nav>
                </div>

                {/* Desktop Layout — matches Figma: logo left, centered glass pill, right-side CTAs */}
                <div className="hidden lg:flex items-center relative h-[58px]">
                    {/* Left — Logo */}
                    <div className="flex items-center">
                        <Link to="/">
                            <img src="/branding/logo_blue.svg" alt="Hush" className="h-[35px] w-auto cursor-pointer" />
                        </Link>
                    </div>

                    {/* Center — Glass pill (654x58) with 5 nav links + inline Contact CTA */}
                    <nav
                        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: '8px 16px',
                            gap: '30px',
                            height: '58px',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            borderRadius: '14px',
                            backdropFilter: 'blur(30px)',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: '8px 40px',
                                gap: '30px',
                                height: '42px',
                                borderRadius: '60px',
                            }}
                        >
                            {[
                                { to: '/', label: 'Home' },
                                { to: '/features', label: 'Features' },
                                { to: '/candidates', label: 'Candidates' },
                                { to: '/employers', label: 'Employers' },
                                { to: '/about', label: 'About' },
                            ].map(({ to, label }) => (
                                <Link
                                    key={to}
                                    to={to}
                                    className="hover:text-white transition-colors duration-200"
                                    style={{
                                        fontFamily: 'Manrope',
                                        fontWeight: 400,
                                        fontSize: '16px',
                                        lineHeight: '26px',
                                        letterSpacing: '-0.0001em',
                                        color: '#FFFFFF',
                                        textDecoration: 'none',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {label}
                                </Link>
                            ))}
                        </div>
                        <Link to="/contact" style={inlinePillCtaStyle}>
                            Contact
                        </Link>
                    </nav>

                    {/* Right — Auth CTAs (Log in: transparent glass, Apply: filled glass) */}
                    <div className="flex items-center ml-auto" style={{ gap: '24px' }}>
                        <Link to="/auth/login" style={secondaryAuthStyle}>
                            Log in
                        </Link>
                        <Link to="/auth/apply" style={primaryAuthStyle}>
                            Apply
                        </Link>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Navbar;
